import React, { useMemo } from 'react';
import { DesignSystemShadowProvider } from './DesignSystemShadowProvider';
import {
  RegionCurrencyContextProvider,
  UserLoggedInKeyProvider,
  WishlistCustomerKeyProvider,
  personalizationAPIClient,
} from '@skin-seoul/service';
import {
  CookiePersonalizationStorageAdapter,
  PersonalizationClient,
  PersonalizationProvider,
  webCookieStore,
} from '@skin-seoul/utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ShortcodePersonalizationAuthSync } from './ShortcodePersonalizationAuthSync';
import { ShortcodeCookieConsentProvider } from './ShortcodeCookieConsentProvider';
import { ShortcodeAnalyticsPlatformProvider } from './ShortcodeAnalyticsPlatformProvider';

type ShortcodeProviderProps = {
  children?: React.ReactNode;
};

const queryClient = new QueryClient();

const ShortcodeProvider = ({ children }: ShortcodeProviderProps) => {
  const personalizationClient = useMemo(
    () =>
      new PersonalizationClient({
        resolveActor: (currentActorId) => personalizationAPIClient.resolveActor(currentActorId),
        sendEvent: (payload) => personalizationAPIClient.trackEvent(payload),
        // TODO: When mobile app sets cookies with domain=".skinseoul.com",
        // pass { domain: '.skinseoul.com' } so logout cleanup removes matching cookies
        storage: new CookiePersonalizationStorageAdapter(webCookieStore),
        skipResolveWhenSessionValid: true,
      }),
    [],
  );

  return (
    <DesignSystemShadowProvider>
      <QueryClientProvider client={queryClient}>
        <RegionCurrencyContextProvider>
          <ShortcodeCookieConsentProvider>
            <UserLoggedInKeyProvider>
              <PersonalizationProvider client={personalizationClient} autoInitialize={false}>
                <ShortcodePersonalizationAuthSync />
                <ShortcodeAnalyticsPlatformProvider>
                  <WishlistCustomerKeyProvider>{children}</WishlistCustomerKeyProvider>
                </ShortcodeAnalyticsPlatformProvider>
              </PersonalizationProvider>
            </UserLoggedInKeyProvider>
          </ShortcodeCookieConsentProvider>
        </RegionCurrencyContextProvider>
      </QueryClientProvider>
    </DesignSystemShadowProvider>
  );
};

export default ShortcodeProvider;
